import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import HomeBrokerPowerByJoveDialog from '@/quote/components/dialogs/homeBrokerPowerByJove/index';
import QuoteDisclaimerView from '@/quote/components/quoteDisclaimer/index';
import { useBrokerProductInfo } from '@/quote/hooks/broker/useBrokerProductInfo';
import useHandleQuoteFromValidation from '@/quote/hooks/quoteInfo/useHandleQuoteFromValidation';
import { useQuotePromotion } from '@/quote/hooks/quoteInfo/useQuotePromotion';
import { useChannelSource } from '@/quote/hooks/useChannelSource';
import { useCountryInfo } from '@/quote/hooks/useCountryInfo';
import { useGlobalLogo } from '@/quote/hooks/useGlobalLogo';
import { useQuoteLocale } from '@/quote/hooks/useQuoteLocale';
import { getCookieValue } from '@/quote/util/cookies';
import { saveHomePageUrlParamsInfo } from '@/quote/util/quote';
import { clearAction, updateAction } from '@/quote/util/update-action';
import { QuoteFromPageSourceEnum } from '@jove-frontend/enums';
import {
  Button,
  GoodsInTransitKey,
  InsuranceType,
  InsurerCode,
  JoveLogo,
  LoadingMask,
  PromotionEntityType,
  QuoteProceessUrlPath,
  RadioGroupList,
  getBusinessInsuranceTypes,
  getLocalTranslation,
  hiredInPlantKey,
  useLocalTranslation,
} from '@jove-frontend/uikit';
import axios from 'axios';
import AutomatedModal from 'libs/uikit/src/ui/AutomatedModal';
import { useStateMachine } from 'little-state-machine';
import { cloneDeep } from 'lodash';
import qs from 'qs';
import { Trans } from 'react-i18next';
import Header from '../components/Header';
import NotCoveredPopUp from '../components/NotCoveredPopUp';
import { changeBodyBackgroundColor } from '../util/global';

const ENV = process.env.NX_CUSTOM_ENV;
const PROD_COOKIE_VALUE = process.env.NEXT_PUBLIC_PROD_TEST_KEY_FLAG;
const PROD_COOKIE = 'prodKey';

export const Homepage: NextPage = () => {
  const router = useRouter();
  const { token, candidate_id, validation_id, contract_id, notCovered, display_insurance, providerCode, promotion_id } = router.query as {
    token: string;
    candidate_id: string;
    validation_id: string;
    contract_id: string;
    notCovered: string;
    display_insurance: string;
    providerCode: InsurerCode;
    promotion_id?: string;
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAutomated, setShowAutomated] = useState<boolean>(false);
  const [candidateData, setCandidateData] = useState<any>(null);
  const [contractData, setContractData] = useState<any>(null);
  const [validationData, setValidationData] = useState<any>(null);
  const [businessInsuranceTypes] = useState(getBusinessInsuranceTypes().filter((item) => item.value !== hiredInPlantKey));
  const [insuranceProducts, setInsuranceProducts] = useState(businessInsuranceTypes);
  const [showNotCoveredPopUp, setShowNotCoveredPopUp] = useState(notCovered === 'true');
  const { state, actions, getState } = useStateMachine({
    updateAction,
    clearAction,
  });
  const [insuranceType, setInsuranceType] = useState<string>('');
  const {
    requestCandidateInfo,
    requestQuoteValidationRequirements,
    requestContractInfo,
    storeRelatedDateInTheStoreState,
    requestQuoteValidationInfo,
  } = useHandleQuoteFromValidation();
  const { requestUserCountryCode } = useCountryInfo();
  const { isGreatAmericanChannel, isNewGoodsInTransitChannel, isMspChannel } = useChannelSource();
  const [autoEnterGetStartInsurance, setAutoEnterGetStartInsurance] = useState('');
  const { saveQuoteChannel } = useGlobalLogo();
  const { $t } = useLocalTranslation();
  const currentApiCountry = useRef('');
  const headerRef = useRef(null);
  const { getCurrentUseCountryCode } = useQuoteLocale();
  const [promotionInfo, setPromotionInfo] = useState(null);
  const { requestQuotePromotionDetailInformation } = useQuotePromotion();
  const [quoteValidationInfo, setQuoteValidationInfo] = useState(null);
  const [showBrokerPowerByJoveDialog, setShowBrokerPowerByJoveDialog] = useState(false);
  const { fileLoading, brokerProductFileInfoMap, isTripartiteProductFile, openTermsOfBusinessFileAction, openPrivacyPolicyFileAction } =
    useBrokerProductInfo();

  /**
   * effects
   */
  useEffect(() => {
    actions.clearAction();
    makeRequests();
  }, []);

  useEffect(() => {
    if ($t) {
      handleDisplayInsurance();
    }
  }, [$t]);

  useEffect(() => {
    if (autoEnterGetStartInsurance) {
      setTimeout(() => {
        handleGetStarted(autoEnterGetStartInsurance);
      }, 0);
    }
  }, [autoEnterGetStartInsurance]);

  useEffect(() => {
    if (validationData) {
      const coverNames = validationData.map((item) => item.validation_requirement.extracted_data.cover_slug) as string[];

      actions.updateAction({
        ...state,
        failedCoverNames: coverNames,
      });

      let products = businessInsuranceTypes.filter((item) => coverNames.includes(item.value));

      const { country_code } = candidateData || {};
      // // Temporary demand; If git is present in the EU, business insurance is not displayed
      // const temporarayFlag =
      //   coverNames.includes(GoodsInTransitKey) &&
      //   EuropeanUnionList.includes(country_code);

      // Temporary demand; If git is present, business insurance is not displayed
      const temporarayFlag = coverNames.includes(GoodsInTransitKey);

      if ((coverNames.includes('public-liability') || coverNames.includes('professional-indemnity')) && !temporarayFlag) {
        products = [...products, ...(businessInsuranceTypes as any).filter((item) => item.value === 'business-insurance')];
      }

      modifyInsuranceProducts(products);
      handleAutoEnterPersonalDetailPage(products);
    }
  }, [validationData]);

  useEffect(() => {
    if (insuranceProducts.length === 1) {
      setInsuranceType(insuranceProducts[0].value);
    }
  }, [insuranceProducts]);

  useEffect(() => {
    if (!!promotionInfo?.promotionType && insuranceProducts?.length === 1) {
      setInsuranceType(insuranceProducts[0].value);
      setAutoEnterGetStartInsurance(insuranceProducts[0].value);
    }
  }, [promotionInfo, insuranceProducts]);

  /**
   * methods
   */

  const makeFromValidationAllRequests = async () => {
    setIsLoading(true);

    // Temporary keep the original code judgment logic
    let tempCountryCode = '';
    if (router.isReady && token) {
      const candidateInfo = await requestCandidateInfo({ candidate_id, token });
      setCandidateData(candidateInfo);
      tempCountryCode = candidateInfo?.country_code;

      const contractInfo = await requestContractInfo({
        contract_id,
        token,
      });
      setContractData(contractInfo);
    }
    dynamicUpdateCurrentLocale(tempCountryCode);

    if (validation_id) {
      const validationInfo = await requestQuoteValidationRequirements({
        validation_id,
        token,
      });
      const tempQuoteValidationInfo = await requestQuoteValidationInfo({ validation_id });
      setQuoteValidationInfo(tempQuoteValidationInfo);
      setValidationData(validationInfo);
    }

    setIsLoading(false);

    // Temporary
    tempAutoEnterPersonalDetailPage();
  };

  async function handleGetStarted(iType?: string) {
    temporarySaveInsuranceTestInfo();

    if (!!router.query?.hiscox) {
      sessionStorage.setItem('test-hiscox', 'true');
    }

    const isStaging = ENV === 'staging';
    const hasProdCookie = getCookieValue(PROD_COOKIE) === PROD_COOKIE_VALUE;
    const shouldShowProdFlow = !isStaging || hasProdCookie;

    try {
      let pushUrl = '';
      let assignBusinessInfo = false;
      let legacyQuoteData = undefined;

      let realInsuranceType = InsuranceType.BUSINESS_INSURANCE;
      const selectInsuranceType = iType || insuranceType;
      switch (selectInsuranceType) {
        case 'business-insurance':
          // pushUrl = !shouldShowProdFlow
          //   ? QuoteProceessUrlPath.QuoteProcess_PersonalDetails
          //   : '/business-insurance/personal-details';

          // if (!shouldShowProdFlow) assignBusinessInfo = true;

          pushUrl = QuoteProceessUrlPath.QuoteProcess_PersonalDetails;
          assignBusinessInfo = true;

          realInsuranceType = InsuranceType.BUSINESS_INSURANCE;
          break;
        case 'hired-in-plant':
          assignBusinessInfo = true;
          pushUrl = '/hired-in-plant/insurance-type';

          realInsuranceType = InsuranceType.CONSTRUCTION_INSURANCE;
          break;
        case GoodsInTransitKey:
          // if (isNewGoodsInTransitChannel()) {
          //   pushUrl = QuoteProceessUrlPath.QuoteProcess_PersonalDetails;
          // } else {
          //   pushUrl = '/goods-in-transit';
          // }
          pushUrl = QuoteProceessUrlPath.QuoteProcess_PersonalDetails;

          realInsuranceType = InsuranceType.GOODS_IN_TRANSIT_INSURANCE;
          break;
        default:
          return;
      }

      storeRelatedDateInTheStoreState({
        candidateData,
        validationData,
        contractData,
        legacyQuoteData,
        assignBusinessInfo,
        insuranceType: realInsuranceType,
        promotionInfo,
        quoteValidationInfo,
      });
      saveHomePageUrlParamsInfo(router.query);

      const { quoteSourceChannel } = router.query;
      const queryParams = {
        insuranceType: realInsuranceType,
        quoteSourceChannel,
      };

      return router.push(`${pushUrl}?${qs.stringify(queryParams)}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Error message:', error.message);

        return error.message;
      } else {
        console.error('Unexpected error:', error);

        return 'An unexpected error occurred';
      }
    }
  }

  const makeRequests = () => {
    tempSpecialHandleUSInfo();
    handleQuoteFromPageSource();
    makeFromValidationAllRequests();
    makeAllPromotionRequests();
    saveQuoteChannel();
    handleDisplayInsurance();
  };

  const handleQuoteFromPageSource = () => {
    const { quoteSourceChannel, websiteOpenQuoteIframe } = router.query;
    if (quoteSourceChannel === QuoteFromPageSourceEnum.websiteRecruitment) {
      sessionStorage.setItem('jove-websiteRecruitment', 'true');
    } else {
      sessionStorage.removeItem('jove-websiteRecruitment');
    }
    if (websiteOpenQuoteIframe) {
      sessionStorage.setItem('website-open-quote-iframe', 'true');
    }
  };

  const tempSpecialHandleUSInfo = async () => {
    const country = await requestUserCountryCode();
    if (country === 'US' && !judgeWhetherLimitDisplayedInsurance()) {
      currentApiCountry.current = country;
      modifyInsuranceProducts([...insuranceProducts]);
    }
  };

  const tempAutoEnterPersonalDetailPage = () => {
    setIsLoading(true);

    temporarySaveInsuranceTestInfo();

    if (isGreatAmericanChannel()) {
      setInsuranceType(businessInsuranceTypes[2].value);
      setAutoEnterGetStartInsurance(businessInsuranceTypes[2].value);
    } else if (isMspChannel()) {
      actions.updateAction({
        ...state,
        temporaryUseFormRecruitmentOpenFromValidation: true,
      });
      setInsuranceType(businessInsuranceTypes[0].value);
      setAutoEnterGetStartInsurance(businessInsuranceTypes[0].value);
    } else {
      setIsLoading(false);
    }
  };

  const temporarySaveInsuranceTestInfo = () => {
    // Temporary
    if (router.query['ga']) {
      sessionStorage.setItem('channel-ga', 'true');
    }
    if (router.query['acce']) {
      sessionStorage.setItem('channel-acce', 'true');
    }
    if (router.query['git']) {
      sessionStorage.setItem('channel-git', 'true');
    }
  };

  const handleDisplayInsurance = () => {
    if (judgeWhetherLimitDisplayedInsurance()) {
      const coverNames = display_insurance.split(',');
      const products = businessInsuranceTypes.filter((item) => coverNames.includes(item.value));
      modifyInsuranceProducts(products);
    }
  };

  const judgeWhetherLimitDisplayedInsurance = () => {
    return !validation_id && !!display_insurance;
  };

  const handleAutoEnterPersonalDetailPage = (products = []) => {
    const autoEnterStr = router.query['autoEnter'];
    if (autoEnterStr) {
      if (autoEnterStr === 'true' && products.length === 1) {
        setInsuranceType(products[0].value);
        setAutoEnterGetStartInsurance(products[0].value);
      } else {
        const temp = products.find((item) => item.value === autoEnterStr);
        if (temp) {
          setInsuranceType(temp.value);
          setAutoEnterGetStartInsurance(temp.value);
        }
      }
    }
  };

  const modifyInsuranceProducts = (list) => {
    const produceList = getBusinessInsuranceTypes().filter((item) => item.value !== hiredInPlantKey);
    list.forEach((item) => {
      const temp = produceList.find((aItem) => aItem.value === item.value);
      if (currentApiCountry.current === 'US' && item.value === GoodsInTransitKey) {
        item.label = getLocalTranslation().$t('pages:home.motor_truck_cargo_insurance');
        item.description = getLocalTranslation().$t('pages:home.motor_truck_cargo_insurance_desc');
      } else if (temp) {
        item.label = temp.label;
        item.description = temp.description;
      }
    });
    setInsuranceProducts(list);
  };

  const dynamicUpdateCurrentLocale = async (countryCode) => {
    let useCountryCode = await getCurrentUseCountryCode({ countryCode });
    headerRef.current?.modifyProjectLanguage({
      countryCode: useCountryCode,
    });
  };

  const localeChangeAction = () => {
    modifyInsuranceProducts(cloneDeep(insuranceProducts));
  };

  const makeAllPromotionRequests = async () => {
    if (promotion_id) {
      setIsLoading(true);
      const promotionInfo = await requestQuotePromotionDetailInformation({ promotionId: promotion_id });
      if (promotionInfo) {
        actions.updateAction({
          ...getState(),
          promotionInfo,
        });
        setPromotionInfo(promotionInfo);
        updateBodyBackgroundColor(promotionInfo);
      }
      setIsLoading(false);
    }
  };

  const updateBodyBackgroundColor = (promotionInfo) => {
    const color = promotionInfo?.payload?.quote_process_settings?.quote_website_body_background_color;
    if (color) {
      changeBodyBackgroundColor(color);
      sessionStorage.setItem('jove-body-background-color', color);
      // let data: any = localStorage.getItem('jove-body-background-color');
      // try {
      //   if (data) {
      //     data = JSON.parse(data);
      //     data[promotionInfo.id] = color;
      //   } else {
      //     data = {};
      //   }
      // } catch (_) {}
      // localStorage.setItem('jove-body-background-color', JSON.stringify(data));
    }
  };

  const brokerPoweredByJoveClickLearnMoreAction = () => {
    setShowBrokerPowerByJoveDialog(true);
  };

  const closeBrokerPoweredByJoveClickLearnMoreAction = () => {
    setShowBrokerPowerByJoveDialog(false);
  };

  return (
    <div className="flex h-full flex-col">
      {(isLoading || fileLoading) && <LoadingMask />}
      <Header ref={headerRef} localeChange={localeChangeAction} alwaysShowLocaleSwitch />
      <div className="flex flex-1 flex-col bg-white px-3 py-3">
        <div className="flex h-full flex-col justify-between">
          <div className="mt-4 space-y-6 p-5">
            {promotionInfo?.promotionType === PromotionEntityType.BROKER_PROMOTION ? (
              <h4 className="flex items-center justify-center text-2xl font-black text-gray-800">Get covered in 90 seconds</h4>
            ) : (
              <h4 className="flex items-center justify-center text-2xl font-black text-gray-800">
                {$t('pages:home.get_covered_with')} <JoveLogo className="ml-2 h-16 w-auto" />
              </h4>
            )}
            <RadioGroupList
              options={insuranceProducts}
              value={insuranceType}
              onChange={(e) => {
                setInsuranceType(e);
              }}
            />

            <Button color="primary" className="w-full" onClick={() => handleGetStarted()} disabled={!insuranceType}>
              {$t('pages:home.get_started')}
            </Button>

            <p className="mt-5 text-center text-xs leading-5">
              {isTripartiteProductFile && !brokerProductFileInfoMap?.toba?.isCustom && !brokerProductFileInfoMap?.privacy?.isCustom ? null : (
                <Trans
                  ns="pages"
                  i18nKey="home.terms_and_privacy_policies"
                  components={{
                    a: (
                      <a
                        href="https://www.jove.co/legal/terms-of-business-agreement"
                        target="_blank"
                        className="text-brand-primary font-link underline"
                        rel="noreferrer"
                        onClick={openTermsOfBusinessFileAction}
                        style={{ display: !isTripartiteProductFile || brokerProductFileInfoMap?.toba?.isCustom ? 'visible' : 'none' }}
                      />
                    ),
                    a1: (
                      <a
                        href="https://www.jove.co/legal/privacy-policy"
                        target="_blank"
                        className="text-brand-primary font-link underline"
                        rel="noreferrer"
                        onClick={openPrivacyPolicyFileAction}
                        style={{ display: !isTripartiteProductFile || brokerProductFileInfoMap?.privacy?.isCustom ? 'visible' : 'none' }}
                      />
                    ),
                    a2: (
                      <span
                        style={{
                          display:
                            !isTripartiteProductFile || (brokerProductFileInfoMap?.toba?.isCustom && brokerProductFileInfoMap?.privacy?.isCustom)
                              ? 'visible'
                              : 'none',
                        }}
                      />
                    ),
                  }}
                ></Trans>
              )}

              <br />
              <Trans
                ns="pages"
                i18nKey="home.learn_more"
                components={{
                  span: <span role="button" className="text-brand-primary font-bold underline" onClick={() => setShowAutomated(true)} />,
                }}
              ></Trans>
            </p>
          </div>
          <div className="flex flex-col space-y-1">
            <QuoteDisclaimerView className="px-3" />
            {[PromotionEntityType.BROKER_PROMOTION].includes(promotionInfo?.promotionType) && (
              <div className="ml-3 flex flex-row items-center space-x-1 text-xs">
                <span>Powered by Jove -</span>
                <span className="text-brand-primary cursor-pointer font-bold underline" onClick={brokerPoweredByJoveClickLearnMoreAction}>
                  Learn more
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {showAutomated ? <AutomatedModal setShowAutomated={setShowAutomated} /> : null}
      {showNotCoveredPopUp && <NotCoveredPopUp onClose={setShowNotCoveredPopUp} />}
      {showBrokerPowerByJoveDialog && <HomeBrokerPowerByJoveDialog close={closeBrokerPoweredByJoveClickLearnMoreAction} />}
    </div>
  );
};

export default Homepage;
